import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isNil from 'lodash/isNil';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import mapValues from 'lodash/mapValues';
import filter from 'lodash/filter';
import merge from 'lodash/merge';
import omit from 'lodash/omit';
import omitBy from 'lodash/omitBy';
import orderBy from 'lodash/orderBy';
import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';
import reduce from 'lodash/reduce';
import set from 'lodash/set';
import size from 'lodash/size';
import values from 'lodash/values';
import { defaultPromiseState, defaultTableResultsState } from '~/config';
import normalizeFieldValue from '~/data/normalizeFieldValue';
import normalizeFilters from '~/data/normalizeFilters';
import identify from '../identify';

import { prepareEditField, prepareFilter, prepareGrid, prepareGridVisibilityFields, prepareGridControlGroups, prepareOptions, prepareRelationshipModelFields, prepareSubviews } from "./typedDataPreparer";

export * from "./typedDataPreparer";

import { modify } from 'mythil/object';
import { getUniqueKey } from '~/lib/uniqueId';
import {
    getAllResultParams,
    getArchiveFilter,
    getEmptyFilterValues,
    getPredefinedViews,
    getModel,
    getModelTitle,
    getSettings,
} from '~/stateGetter';
import {
    presentAppliedFilterValuesObject,
    presentFilteredViewsWithRelativeDates,
    presentFilters,
    presentRouteViews,
} from '~/statePresenter';
import { getFitleredViewState } from '~/StateGetters/FilteredViewStateGetters';

// TODO: Split up file
export const prepareColumn = (column) => {
    return {
        title: column.title,
        name: column.column_name,
        sortable: column.sortable,
        visible: column.visible,
        align: column.align,
        highlight: column.highlight,
        hidden: !!column.hidden,
        fieldGroup: column.fieldGroup,
        autoHide: !!column.autoHide,
        tooltip: column.tooltip,
        columnVisible: column.columnVisible,
        interval: column.interval,
        timerDueText: column.due_text,
    };
};

export const indexResults = (results) => {
    let indexedResults = {};
    for (let i = 0; i < results.length; i++) {
        let row = results[i];
        row.order = i;
        indexedResults[row.id.raw] = row;
    }

    return indexedResults;
};

export const prepareResults = (resultsValue) => {
    const { administrator_messages, results, ...restProps} = resultsValue;

    return {
        ...restProps,
        results: indexResults(results),
        messages: prepareMessages(administrator_messages.messages, "result"),
    }
}

export const prepareValue = (field, inputType, value) => {
    let valueData = {
        value: normalizeFieldValue(inputType, field.type, value),
        changed_at: null,
        previousValue: undefined,
    };

    if (inputType === identify.EDITFIELD) {
        if (
            (valueData.value !== null && valueData.value !== '') ||
            (isArray(valueData.value) && valueData.value.length > 0)
        ) {
            valueData.changed_at = null;
        }
    }

    return valueData;
};

export const extractFieldValue = (field, inputType, options = {}) => {
    const valueProps = {
        [identify.FILTERFIELD]: {
            valueKey: 'defaultValue',
        },
        [identify.EDITFIELD]: {
            valueKey: 'value',
        },
    };

    const nv = (val) => normalizeFieldValue(inputType, field.type, val);
    const { valueKey } = get(valueProps, inputType, {});

    let preparedVal = { value: nv(get(field, valueKey)), changed_at: null, previousValue: undefined };

    if (options.hasMinMax && field.min_max) {
        preparedVal = {
            value: { min: nv(field.min_value), max: nv(field.max_value) },
            changed_at: null,
            previousValue: undefined,
        };
    }

    return preparedVal;
};

export const extractFilterValue = (field, inputType, options = {}) => {
    const preparedVal = extractFieldValue(field, inputType, options);

    const isNotNilOrBlank = (value) => !isNil(value) && value !== '';
    preparedVal.created_at =
        isNotNilOrBlank(field.defaultValue) ||
        (options.hasMinMax && (isNotNilOrBlank(field.min_value) || isNotNilOrBlank(field.max_value)))
            ? new Date().getTime()
            : null;

    return preparedVal;
};

export const prepareEditFieldValues = ({ fields, inputType, getFieldValue }) => {
    let rawValues = {};

    for (let fieldName in fields) {
        if (!fields.hasOwnProperty(fieldName)) {
            continue;
        }

        let field = fields[fieldName];
        let valueData = prepareValue(field, inputType, getFieldValue(fieldName));

        rawValues[fieldName] = valueData;
    }
    return rawValues;
};



export const prepareAutocompleteOptions = (options) => {
    return {
        query: null,
        autocomplete: {
            value: prepareOptions(options),
            ...defaultPromiseState,
        },
    };
};

export const getFieldsOptions = (fields) => {
    return reduce(
        fields,
        (options, field, fieldName) => {
            if (!field.options) {
                return options;
            }

            if (!field.autocomplete) {
                options[fieldName] = { query: null };
                return options;
            }

            options[fieldName] = prepareAutocompleteOptions(get(field, 'options', []));
            return options;
        },
        {}
    );
};

export const preparePredefinedFilters = (model) => {
    const filters = normalizeFilters(model.filters),
        initialFilters = pickBy(
            mapValues(filters, (filter) => extractFilterValue(filter, identify.FILTERFIELD, { hasMinMax: true })),
            'created_at'
        );

    let predefinedViews = mapValues(model.predefined_views, ({ name, ...view }) => ({
        viewKey: name,
        ...view,
        filters: initialFilters,
    }));

    mapValues(filters, (filter, key) => {
        if (!size(filter.views)) {
            return;
        }
        mapValues(filter.views, ({ value, min_value, max_value }, viewKey) => {
            const filterValue = extractFilterValue(
                { ...filter, defaultValue: value, min_value, max_value },
                identify.FILTERFIELD,
                { hasMinMax: true }
            );
            set(predefinedViews, `${viewKey}.filters.${filter.field_name}`, filterValue);
        });
    });

    return predefinedViews;
};


export const prepareModel = (model) => {
    let resultParams = {
        perPage: model.rows_per_page,
        sortOptions: model.sortOptions,
    };
    let preparedItemData = initItemData(model);
    let subviews = prepareSubviews(model.subviews);
    let preparedGrid = prepareGrid(model, {...subviews} );

    const fieldGridControlSources = prepareGridControlGroups(preparedGrid);
    let normalizedFilters = normalizeFilters(model.filters);
    let predefinedViews = preparePredefinedFilters(model);

    const defaultStateModel = {
        isInitialized: false,
        modelId: null,
        parentModelId: null,
        modelName: model.model_name,
        subviewName: null,
        view: identify.VIEW_TABULAR,
        previousView: null,
        closeModelId: null,

        activeRelationshipField: null,

        newRecordTitle: model.new_record_title || '',

        defaultGrid: model.defaultGrid ?? "default",

        filterGroups: mapValues(keyBy(model.filterGroups, 'name'), (g) => {
            return g.name === 'default';
        }),

        filterOptions: getFieldsOptions(normalizedFilters),

        activeView: {},
        resultParams: {
            ...resultParams,
            filters: mapValues(normalizedFilters, (filter) =>
                extractFilterValue(filter, identify.FILTERFIELD, { hasMinMax: true })
            ),
            predefinedViews,
        },

        results: { ...defaultTableResultsState },

        customActionResult: {
            ...defaultPromiseState,
            value: {
                messages: [],
            },
        },

        item: {
            id: null,
            options: cloneDeep(preparedItemData.options),
            values: cloneDeep(preparedItemData.values),
        },

        itemData: {
            ...defaultPromiseState,
            value: preparedItemData,
        },

        saveResult: {
            ...defaultPromiseState,
            value: {
                messages: [],
            },
        },

        deleteResult: {
            ...defaultPromiseState,
            value: {
                messages: [],
            },
        },

        itemCustomActionResult: {
            ...defaultPromiseState,
            value: {
                messages: [],
            },
        },
    };

    return {
        dataModel: {
            defaultLevel: model.defaultLevel,
            primaryKey: model.primary_key,
            singleTitle: model.model_single,
            title: model.model_title,
            modelName: model.model_name,
            isGlobalSearchEnabled: model.global_search_enabled,
            searchFilter: model.searchFilter,
            searchOrder: model.searchOrder,
            tooltips: model.tooltips,
            createPath: model.create_path,
            archiveFilter: model.archiveFilter,
            filterGroups: mapValues(keyBy(model.filterGroups, 'name'), (g) => {
                return {
                    ...pick(g, ['name', 'title', 'icon', 'subgroup', 'description']),
                    iconFamily: g.icon ? get(g, 'icon_family') : undefined,
                    order: g.builder_order,
                    showLabel: !!g.show_label,
                    flatten: g.name === identify.PRIMARY_ACTION_GROUP,
                };
            }),

            calendars: values(
                mapValues(model.calendars, (calendarView, name) => {
                    return {
                        ...calendarView,
                        name,
                    };
                })
            ),

            globalActions: model.global_actions.map(prepareAction),

            emptyFilterValues: mapValues(normalizedFilters, (filter) =>
                extractFilterValue(
                    omit(filter, 'value', 'defaultValue', 'min_value', 'max_value'),
                    identify.FILTERFIELD,
                    { hasMinMax: true }
                )
            ),
            resultParams: {
                filters: mapValues(normalizedFilters, prepareFilter),
                columns: keyBy(map(model.column_model, prepareColumn), 'name'),
                resultInfo: {
                    titleColumn: model.titleColumn,
                    infoColumn: model.infoColumn,
                    bodyColumn: model.bodyColumn,
                },
            },

            item: {
                fieldGrid: preparedGrid,
                fieldGridVisibilitySources: prepareGridVisibilityFields(preparedGrid),
                fieldGridControlSources,
                subviews,
                relationshipFields: filter(preparedItemData.editFields, "canCreate", "model"),
            },

            itemData: initItemData(model),

            initialState: cloneDeep(defaultStateModel),
        },

        stateModel: cloneDeep(defaultStateModel),
    };
};

const prepareAction = (action) => {
    return {
        value: action.action_name,
        group: action.group,
        caption: action.title,
        hasPermission: action.has_permission,
        icon: action.icon,
        iconFamily: action.icon ? action.icon_family : undefined,
        confirmation: !!action.confirmation,
        confirmationMessage: action.confirmation_text || undefined,
        criticalConfirmText: action.critical_confirm_text,
        criticalConfirmQuantity: action.critical_confirm,
        captionActive: get(action, 'messages.active'),
        isActive: false,
        requires_selection: get(action, 'requires_selection'),
        noReload: !!action.no_reload,
        dialogTitle: action.dialogTitle,
        scanQr: action.scanQr,
        grid: action.grid ? action.grid : null,
        tooltip: action.tooltip,
        allowDirty: !!action.allow_dirty,
        order: action.builder_order || 100,
        showLabel: !!action.show_label,
        description: action.description,
        subgroup: action.subgroup,
        enabled: action.enabled,
    };
};

export const initItemData = (model) => {
    let preparedItemData = prepareItemData({
        itemId: null,
        link: null,
        success: true,
        edit_fields: model.edit_fields,
        actions: model.actions,
        action_permissions: model.action_permissions,
        options: reduce(
            model.edit_fields,
            (theOptions, field, fieldName) => {
                if (!field.options) {
                    return theOptions;
                }
                if (!field.autocomplete) {
                    theOptions[fieldName] = { query: null };
                    return theOptions;
                }

                theOptions[fieldName] = prepareAutocompleteOptions(get(field, `${fieldName}.options`, []));

                return theOptions;
            },
            {}
        ),
        values: mapValues(model.edit_fields, 'value'),
        messages: [],
    });

    return preparedItemData;
};

export const prepareMessages = (messages, ...tags) => {
    const now = new Date().toISOString();
    const typeMap = {
        validation_error: "error",
        danger: "error",
    }

    return messages.map((message) => {
        return {
            ...message,
            tags: Array.from(new Set([message.scope ?? 'local', ...tags.filter(Boolean)])),
            created_at: message.created_at ?? now,
            type: message.type,
            status: typeMap[message.type] ?? message.type,
        };
    });
};

export const prepareItemData = (itemData) => {
    if (!itemData.success) {
        return {
            itemId: itemData.itemId,
            success: itemData.success,
            messages: itemData.messages,
        };
    }

    let editFields = mapValues(itemData.edit_fields, prepareEditField),
        initialValues = mapValues(itemData.edit_fields, (field) => extractFieldValue(field, identify.EDITFIELD)),
        options = itemData.options,
        fieldValues = prepareEditFieldValues({
            fields: editFields,
            inputType: identify.EDITFIELD,
            getFieldValue: (fieldName) => {
                const field = get(itemData.edit_fields, fieldName);
                let value = get(itemData.values, fieldName, field.value);

                if (field.group === identify.HIDDEN_FIELD_GROUP) {
                    switch(field.type) {
                        case "select_many":
                            return Array.isArray(value) ? value.sort() : [];
                    }
                }

                return value;
            },
        }),
        actions = itemData.actions.map(prepareAction),
        actionPermissions = itemData.action_permissions,
        messages = itemData.messages;

    return {
        link: itemData.link,
        itemId: itemData.itemId,
        success: true,
        editFields,
        initialValues,
        values: fieldValues,
        actions,
        actionPermissions,
        messages,
        options,
        isNewItem: itemData.prevItemId === identify.NEW_ITEM
    };
};

export const sanitizeRawItemEditFields = ({ administrator_edit_fields, administrator_actions }) => {
    const actionGrids = administrator_actions.reduce((actionGrids, { grid }) => {
        actionGrids[grid] = !!grid;
        return actionGrids;
    }, {});

    return reduce(
        administrator_edit_fields,
        (fields, field, fieldName) => {
            fields[fieldName] = {
                ...field,
                ignored: !!actionGrids[field.group],
            };

            return fields;
        },
        {}
    );
};

export const sanitizeRawItem = (rawItemData) => {
    let success = get(rawItemData, 'administrator_messages.success');


    if (!success) {
        return {
            messages: prepareMessages(rawItemData.administrator_messages.messages, "item"),
            success: false,
        };
    }

    const editFields = sanitizeRawItemEditFields(rawItemData);
    let sanitized = {
        itemId: rawItemData.itemId ? rawItemData.itemId : identify.NEW_ITEM,
        link: rawItemData.admin_item_link ? rawItemData.admin_item_link : null,
        downloadId: rawItemData.download_id,
        redirect: rawItemData.redirect,
        success: true,
        edit_fields: editFields,
        action_permissions: rawItemData.administrator_action_permissions,
        actions: rawItemData.administrator_actions,
        messages: prepareMessages(rawItemData.administrator_messages.messages, "item"),
        options: reduce(
            editFields,
            (theOptions, field, fieldName) => {
                if (!field.options) {
                    return theOptions;
                }
                if (!field.autocomplete) {
                    theOptions[fieldName] = { query: null };
                    return theOptions;
                }

                theOptions[fieldName] = prepareAutocompleteOptions(
                    values(get(rawItemData, `${fieldName}_autocomplete`, field.options))
                );

                return theOptions;
            },
            {}
        ),
        values: reduce(
            editFields,
            (values, field, fieldName) => {
                let fieldValue = get(rawItemData, fieldName);
                if (typeof fieldValue === 'undefined') {
                    return values;
                }
                values[fieldName] = fieldValue;
                return values;
            },
            {}
        ),
    };

    return sanitized;
};

export const sanitizeItemResponse = (response) => {
    return sanitizeRawItem({
        ...response.data,
        itemId: response.itemId,
        downloadId: response.download_id,
        mimeType: response.mime_type,
        redirect: response.redirect,
        administrator_messages: response.administrator_messages,
    });
};

export const prepareCustomActionResponse = (response, actionName, tags = []) => {
    const statusTag = get(response, 'administrator_messages.success', false) ? "customActionSuccess" : "customActionError";

    return {
        messages: prepareMessages(response.administrator_messages.messages, 'customAction', actionName, statusTag, ...tags),
        success: get(response, 'administrator_messages.success', false),
        downloadId: response.download_id,
        redirect: response.redirect,
        mimeType: response.mime_type,
    };
};

export function prepareCurrentView(state, { modelId, modelName, refreshModel, view }) {
    let derivedView = view ?? (refreshModel ? null : get(presentRouteViews(state), modelName)),
        settings = get(getSettings(state), modelName, {}),
        filteredViews = get(settings, `filteredViews`),
        model = getModel(state, modelId),
        activeView =
            derivedView ||
            get(filteredViews, settings.activeFilteredView) ||
            get(state, `ui.sharedViews.${modelName}.${settings.activeFilteredView}`) ||
            get(getPredefinedViews(state, modelId), `${settings.activeFilteredView}`) ||
            orderBy(values(filteredViews), 'createdAt', 'asc')[0] ||
            (filteredViews ? {} : { ...settings }),
            viewKey = activeView?.key || activeView?.viewKey || getUniqueKey(),
            existingView = model.activeView;


    if (existingView && JSON.stringify(existingView) === JSON.stringify(derivedView)) {
        return presentFilteredViewsWithRelativeDates(existingView);
    }

    let resultParams = getAllResultParams(state, modelId),
        initialFilterValues = resultParams.initialFilterValues,
        filters = presentAppliedFilterValuesObject({
            ...resultParams,
            itemId: null,
            filters: keyBy(presentFilters(resultParams), 'name'),
            filterValues: presentFilteredViewsWithRelativeDates(activeView).filters ?? initialFilterValues,
        }),
        archiveFilter = model.subviewName && getArchiveFilter(state, model.modelName);

    if (archiveFilter) {
        initialFilterValues = modify(initialFilterValues, `${archiveFilter}.value`, false);
        initialFilterValues = modify(initialFilterValues, `${archiveFilter}.created_at`, new Date().getTime());
    }

    activeView = modify(activeView, 'viewKey', viewKey);
    activeView = getFitleredViewState(state, modelId).cleanView(activeView);
    activeView = modify(
        activeView,
        'filters',
        filters
            ? mapValues(merge({}, getEmptyFilterValues(state, modelId), filters), (value) => {
                  if (value.created_at !== 0) return value;
                  return modify(value, 'created_at', new Date().getTime());
              })
            : initialFilterValues
    );

    return omitBy(activeView, (x) => x === undefined);
}
